import { useMemo, useState, memo, useEffect } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { ModalButtonProps } from '../../../components/Modal';
import { Modal } from '../../../components/Modal';
import type { WidgetItem } from '../../../domain/company_widgets';
import CompanyWidgetsService from '../../../services/companyWidgets';
import { translate } from '../../../services/i18n';

import { StyledWidgetPickerButton } from './styles';
import { useWidgetPickerTracking } from './useWidgetPickerTracking';
import { WidgetPickerContent } from './WidgetPickerContent';

interface WidgetPickerProps {
  widgets: WidgetItem[];
  onSaveWidgets: (widgets: WidgetItem[]) => void;
}

const WidgetPickerContainer = ({ widgets: initiallySelectedWidgets, onSaveWidgets }: WidgetPickerProps) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [widgets, setWidgets] = useState(initiallySelectedWidgets);

  useEffect(() => {
    setWidgets(initiallySelectedWidgets);
  }, [initiallySelectedWidgets]);

  const { trackChangeWidgets, trackOpenWidgetPicker } = useWidgetPickerTracking();

  const handleClickButton = () => {
    setIsModalOpened(true);
    trackOpenWidgetPicker();
  };

  const handleCloseModal = () => setIsModalOpened(false);

  const handleSaveWidgets = () => {
    trackChangeWidgets(widgets);
    onSaveWidgets(widgets);
    handleCloseModal();
  };

  const confirmButtonProps: ModalButtonProps = useMemo(() => {
    return {
      children: translate('common.save'),
      onClick: handleSaveWidgets,
      disabled: CompanyWidgetsService.widgetsAreEqual(widgets, initiallySelectedWidgets),
      variant: 'primary',
    };
  }, [widgets, initiallySelectedWidgets]);

  return (
    <>
      <StyledWidgetPickerButton variant="ghostDark" onClick={handleClickButton} data-automation="WidgetPickerButton">
        <SWReactIcons iconName="edit-icon" size="xs" iconColor={colorsPalettes.carbon[500]} />
        {translate('company.widget_picker.button')}
      </StyledWidgetPickerButton>
      <Modal
        isOpen={isModalOpened}
        onClose={handleCloseModal}
        heading={translate('company.widget_picker.heading')}
        subheading={translate('company.widget_picker.subheading')}
        footerJustifyContent="flex-end"
        confirmButtonProps={confirmButtonProps}
        cancelButtonProps={{
          children: translate('common.cancel'),
          onClick: handleCloseModal,
          variant: 'flat',
        }}
        contentHorizontalPadding={0}
        data-automation="WidgetPickerModal"
      >
        <WidgetPickerContent widgets={widgets} onSelectWidgets={setWidgets} />
      </Modal>
    </>
  );
};

export const WidgetPicker = memo(WidgetPickerContainer);
